import React from 'react';
import { Card, Row, Col, Statistic, Progress, Spin, Empty, Table, Tag, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useIndexerPerformanceMetrics } from '../../hooks/useIndexerManagement';
import { EndpointMetric } from '../../types/indexer';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

const IndexerPerformancePanel: React.FC = () => {
  const { t: indexerT } = useTranslationWithNamespace('indexer');
  const { data: metrics, isLoading, isError } = useIndexerPerformanceMetrics();

  if (isLoading) {
    return (
      <Card title={indexerT('performance.title', 'Indexer Performance')}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (isError || !metrics) {
    return (
      <Card title={indexerT('performance.title', 'Indexer Performance')}>
        <Empty description={indexerT('performance.noData', 'No performance data available')} />
      </Card>
    );
  }

  // Convert endpoints object to array for table
  const endpointsArray = Object.entries(metrics?.endpoints || {}).map(([key, metric]) => ({
    key,
    ...(metric as any)
  }));

  // Define columns for the endpoints table
  const columns = [
    {
      title: indexerT('performance.endpoint', 'Endpoint'),
      dataIndex: 'endpoint',
      key: 'endpoint',
      render: (text: string, record: EndpointMetric) => (
        <span>
          <Tag color="blue">{record.method}</Tag> {text}
        </span>
      )
    },
    {
      title: indexerT('performance.requests', 'Requests'),
      dataIndex: 'totalRequests',
      key: 'totalRequests',
      sorter: (a: EndpointMetric, b: EndpointMetric) => a.totalRequests - b.totalRequests
    },
    {
      title: indexerT('performance.successRate', 'Success Rate'),
      dataIndex: 'successRate',
      key: 'successRate',
      render: (rate: number) => (
        <Tooltip title={`${rate.toFixed(1)}%`}>
          <Progress
            percent={rate}
            size="small"
            status={rate >= 90 ? 'success' : rate >= 70 ? 'normal' : 'exception'}
          />
        </Tooltip>
      ),
      sorter: (a: EndpointMetric, b: EndpointMetric) => a.successRate - b.successRate
    },
    {
      title: indexerT('performance.responseTime', 'Avg Response Time'),
      dataIndex: 'averageResponseTime',
      key: 'averageResponseTime',
      render: (time: number) => `${time.toFixed(0)} ms`,
      sorter: (a: EndpointMetric, b: EndpointMetric) => a.averageResponseTime - b.averageResponseTime
    },
    {
      title: indexerT('performance.lastUpdated', 'Last Updated'),
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      render: (date: string) => dayjs(date).fromNow()
    }
  ];

  return (
    <Card title={indexerT('performance.title', 'Indexer Performance')}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('performance.totalRequests', 'Total Requests')}
            value={metrics?.totalRequests || 0}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('performance.successRate', 'Success Rate')}
            value={metrics?.successRate || 0}
            precision={1}
            suffix="%"
            valueStyle={{ color: (metrics?.successRate || 0) >= 90 ? '#3f8600' : (metrics?.successRate || 0) >= 70 ? '#faad14' : '#cf1322' }}
            prefix={(metrics?.successRate || 0) >= 90 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('performance.avgResponseTime', 'Avg Response Time')}
            value={metrics?.averageResponseTime || 0}
            suffix="ms"
            precision={0}
            prefix={<ClockCircleOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('performance.lastUpdated', 'Last Updated')}
            value={metrics?.lastUpdated ? dayjs(metrics.lastUpdated).fromNow() : 'Never'}
          />
        </Col>
      </Row>

      <div style={{ marginTop: 24 }}>
        <h3>{indexerT('performance.endpointPerformance', 'Endpoint Performance')}</h3>
        <Table
          columns={columns}
          dataSource={endpointsArray}
          rowKey="key"
          size="small"
          pagination={{ pageSize: 5 }}
        />
      </div>
    </Card>
  );
};

export default IndexerPerformancePanel;
